import React, { useCallback, useMemo } from 'react'
import classNames from 'classnames'
import { motion } from 'framer-motion'
import { When } from 'react-if'
import { Button } from '@/atoms/Button'
import { FlexColumn } from '@/atoms/FlexContainers'
import { PayItForwardOutlineIcon } from '@/atoms/Icons/PayItForwardOutlineIcon'
import { TitleMD, CaptionMD, AsH4, ParagraphMD, TitleLG } from '@/atoms/Text'
import { slugs } from '@/constants/slugs'
import { usePayItForwardOfferData } from '@/contexts/PayItForwardOffersDataContext'
import { formatPrice } from '@/services/PriceFormatter'
import { TheatricalReleaseResult } from '@/services/Theatrical/queries'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { useTranslate } from '@/utils/translate/translate-client'
import { TheatricalPifExpressOffers } from '@/views/PayItForward/TheatricalPayItForwardView/components/TheatricalPifExpressOffers'
import { TheatricalPifCtaSection } from '@/views/PayItForward/TheatricalPayItForwardView/sections/TheatricalPifCtaSection'
import ExternalCta from '@/views/TicketCheckoutViews/components/ExternalCta'
import { PifSectionTitle } from './PifSectionTitle'

export interface TheatricalPifHowYouCanHelpSectionProps {
  theatricalRelease: TheatricalReleaseResult
  shouldShowPifSection: boolean
  setShouldShowPifSection: React.Dispatch<React.SetStateAction<boolean>>
  displayNumberOfTickets?: boolean
  pifNameInCreditsEnabled?: boolean
  theatricalSlug?: string
  showOffers: boolean
  setShowOffers: React.Dispatch<React.SetStateAction<boolean>>
}

const variants = {
  open: { opacity: 1 },
  closed: { opacity: 0, display: 'none' },
}

export const TheatricalPifHowYouCanHelpSection: React.FC<TheatricalPifHowYouCanHelpSectionProps> = ({
  theatricalRelease,
  shouldShowPifSection,
  setShouldShowPifSection,
  displayNumberOfTickets,
  pifNameInCreditsEnabled = false,
  theatricalSlug,
  showOffers,
  setShowOffers,
}) => {
  const { query } = useSafeRouter()

  const { t } = useTranslate('pif')
  const { selectedOffer } = usePayItForwardOfferData()
  const selectedOfferPrice =
    selectedOffer &&
    formatPrice(selectedOffer.price, {
      includeDecimals: false,
      currency: selectedOffer.currency,
    })
  const togglePifSection = useCallback(() => {
    setShowOffers((state) => !state)
  }, [setShowOffers])

  const canShowOffersForOfferLink = useMemo(() => {
    return Boolean(Array.isArray(query.offer) || query.offer)
  }, [query.offer])

  return (
    <>
      <div className="mb-4 flex items-center justify-between md:mb-6">
        <PifSectionTitle>
          {theatricalSlug === slugs.youngWashington
            ? t('helpInspireTheNextGeneration', 'Help Inspire the Next Generation')
            : t('howYouCanHelp', 'How You Can Help')}
        </PifSectionTitle>
        <When condition={shouldShowPifSection}>
          <Button
            className="text-base font-medium text-oxide"
            onClick={() => {
              setShouldShowPifSection(false)
            }}
            variant="plain"
          >
            {t('viewAll', 'View All')}
          </Button>
        </When>
      </div>

      <div
        className={classNames(
          !shouldShowPifSection &&
            'rounded-2xl md:rounded-3xl border border-solid border-core-gray-200 bg-white px-4 py-6 md:py-10 pt-8 md:px-8 md:pb-8 md:pt-[48px]',
        )}
      >
        {showOffers || canShowOffersForOfferLink ? (
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex gap-2">
              <div className="flex h-14 w-14 items-center justify-center rounded-lg bg-core-gray-200">
                <PayItForwardOutlineIcon size={24} color="black" />
              </div>
              <div className="flex flex-col justify-center gap-2">
                <TitleLG color="core-gray-950" weight="semibold">
                  {t('payItForward', 'Pay it Forward')}
                </TitleLG>
                <CaptionMD color="core-gray-600" italic>
                  {theatricalRelease?.title} movie
                </CaptionMD>
              </div>
            </div>
            {selectedOfferPrice && (
              <TitleLG weight="semibold" className="self-start">
                {selectedOfferPrice}
              </TitleLG>
            )}
          </div>
        ) : (
          <FlexColumn className="relative gap-2 ">
            <TitleMD as={AsH4} weight="semibold">
              {t('payItForward', 'Pay it Forward')}
            </TitleMD>
            <ParagraphMD color="core-gray-700">
              {t(
                'provideFreeTicketsForSomeone',
                'Provide free tickets for someone to claim and watch at the movie theaters.',
              )}
            </ParagraphMD>
          </FlexColumn>
        )}

        <motion.div animate={canShowOffersForOfferLink || shouldShowPifSection ? 'open' : 'closed'} variants={variants}>
          <div className="mt-4">
            <TheatricalPifExpressOffers
              theatricalRelease={theatricalRelease}
              isOneTimeOnly
              shouldShowDedicationExperiment={canShowOffersForOfferLink}
              shouldShowPeopleImpacted={displayNumberOfTickets}
              newPifLayout
              pifNameInCreditsEnabled={pifNameInCreditsEnabled}
            />
          </div>
        </motion.div>
        <motion.div animate={shouldShowPifSection ? 'closed' : 'open'} variants={variants}>
          <TheatricalPifCtaSection
            newPifLayout
            pifNameInCreditsEnabled={pifNameInCreditsEnabled}
            theatricalRelease={theatricalRelease}
            togglePifSection={togglePifSection}
            showOffersSection={showOffers}
            displayNumberOfTickets={displayNumberOfTickets}
          />
        </motion.div>
      </div>
      <div>{theatricalRelease?.externalCta && <ExternalCta externalCta={theatricalRelease.externalCta} />}</div>

      {/* TODO: Add share section back in when it's fixed and has share icons again */}
      {/* <div className={classNames(newPifLayout && 'flex flex-col gap-4 md:gap-6')}>
        {newPifLayout && (
          <PifSectionTitle> {t('shareProjectTheatricalV2', 'Share {{name}}', { name: projectName })}</PifSectionTitle>
        )}
        <div
          className={classNames(
            'rounded-2xl border border-solid border-core-gray-200 bg-white px-4 py-6 md:py-10',
            !newPifLayout && 'my-4 mt-8',
          )}
        >
          <TheatricalPifShareSection
            newPifLayout={newPifLayout}
            theatricalSlug={theatricalRelease?.theatricalSlug}
            projectName={theatricalRelease?.title as string}
            showYourImpact
            shareUrl={asPath}
          />
        </div>
      </div> */}
    </>
  )
}
